import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, MapPin, X, ArrowRight, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useGeolocation from '../../hooks/useGeolocation';

const SearchModal = ({ 
  isOpen, 
  onClose, 
  searchQuery, 
  setSearchQuery, 
  location, 
  setLocation
}) => {
  const [focusedInput, setFocusedInput] = React.useState(null);
  const navigate = useNavigate();
  const { address, error: geoError, loading: geoLoading, getCurrentLocation } = useGeolocation();

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (location) params.append('location', location);
    onClose();
    navigate(`/search?${params.toString()}`);
  };

  const handleClear = () => {
    setSearchQuery('');
    setLocation('');
  };

  const handleUseCurrentLocation = async () => {
    await getCurrentLocation();
  };

  // Auto-fill location when address is detected
  useEffect(() => {
    if (address && !location) {
      setLocation(address);
    }
  }, [address, location, setLocation]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] overflow-hidden"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm md:backdrop-blur-none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%'
        }}
      />
      
      {/* Modal Container */}
      <div 
        className="absolute inset-0 flex items-end md:items-center justify-center pointer-events-none overflow-hidden"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%'
        }}
      >
        <div 
          className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-lg transform transition-all duration-300 pointer-events-auto md:mx-4 flex flex-col h-full md:h-auto max-h-full md:max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
          style={{ 
            animation: 'slideUp 0.3s ease-out'
          }}
        >
        {/* Mobile Drag Handle */}
        <div className="md:hidden flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
        </div>

        {/* Modal Header */}
        <div className="px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-4 sm:pb-6 flex items-start sm:items-center justify-between flex-shrink-0">
          <div className="flex-1 pr-4">
            <h3 className="text-xl sm:text-2xl font-light text-gray-900 mb-1">Search for Vendors</h3>
            <p className="text-xs sm:text-sm font-light text-gray-500">Find the perfect match for your event</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 sm:p-2.5 hover:bg-gray-100 rounded-full transition-all duration-200 flex-shrink-0 mt-1 sm:mt-0"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 space-y-4 sm:space-y-6">
          {/* Search Input */}
          <div className="relative group">
            <label className="block text-xs font-light text-gray-500 mb-2 sm:mb-3 uppercase tracking-wider">
              What are you planning?
            </label>
            <div className="relative">
              <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <Search className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 ${
                  focusedInput === 'search' ? 'text-gray-900 scale-110' : 'text-gray-400'
                }`} />
              </div>
              <input
                type="text"
                placeholder="e.g., Wedding, Corporate Event"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setFocusedInput('search')}
                onBlur={() => setFocusedInput(null)}
                className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 text-sm sm:text-base text-gray-900 border-2 rounded-xl transition-all duration-300 font-light placeholder:text-gray-400 ${
                  focusedInput === 'search' 
                    ? 'border-gray-900 bg-white shadow-lg' 
                    : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-white'
                } focus:outline-none`}
              />
            </div>
          </div>

          {/* Location Input */}
          <div className="relative group">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <label className="block text-xs font-light text-gray-500 uppercase tracking-wider">
                Where?
              </label>
              <button
                onClick={handleUseCurrentLocation}
                disabled={geoLoading}
                className="flex items-center gap-1 sm:gap-1.5 text-xs text-gray-600 hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Navigation className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${geoLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{geoLoading ? 'Detecting...' : 'Use Current Location'}</span>
                <span className="sm:hidden">{geoLoading ? 'Detecting...' : 'Current'}</span>
              </button>
            </div>
            <div className="relative">
              <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <MapPin className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 ${
                  focusedInput === 'location' ? 'text-gray-900 scale-110' : 'text-gray-400'
                }`} />
              </div>
              <input
                type="text"
                placeholder="City, region, or venue"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onFocus={() => setFocusedInput('location')}
                onBlur={() => setFocusedInput(null)}
                className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 text-sm sm:text-base text-gray-900 border-2 rounded-xl transition-all duration-300 font-light placeholder:text-gray-400 ${
                  focusedInput === 'location' 
                    ? 'border-gray-900 bg-white shadow-lg' 
                    : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-white'
                } focus:outline-none`}
              />
            </div>
            {geoError && (
              <p className="mt-2 text-xs text-red-500">{geoError}</p>
            )}
          </div>

        </div>
      
        {/* Modal Footer */}
        <div className="bg-gray-50/50 border-t border-gray-100 px-4 sm:px-6 md:px-8 py-4 sm:py-6 rounded-b-3xl flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <button
            onClick={handleClear}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-600 rounded-xl font-light hover:border-gray-400 hover:bg-white transition-all duration-200 text-sm sm:text-base"
          >
            Clear All
          </button>
          <button
            onClick={handleSearch}
            className="flex-1 w-full sm:w-auto py-2.5 sm:py-3 bg-gray-900 text-white rounded-xl font-light hover:bg-gray-800 hover:shadow-lg active:scale-95 transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Search className="w-4 h-4" />
            <span>Search Vendors</span>
            <ArrowRight className="w-4 h-4 hidden sm:block" />
          </button>
        </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default SearchModal;

